import { useEffect, useState, type CSSProperties } from "react";
import QRCode from "qrcode";
import type { CanvasElement } from "../types";
import { GRID_UNIT_PX } from "../gridMath";
import { generateBarcodeSvg } from "../utils/barcode";

export function buildSampleValue(label: string): string {
  return `[نمونه: ${label}]`;
}

export function buildSampleTableRows(
  columns: { key: string; label: string }[],
  rowCount = 3
): Array<Record<string, string>> {
  return Array.from({ length: rowCount }, (_, i) =>
    Object.fromEntries(columns.map((c) => [c.key, `${buildSampleValue(c.label)} ${i + 1}`]))
  );
}

interface TableColumnSchema {
  key: string;
  label: string;
}

interface Props {
  element: CanvasElement;
  /** Real report data keyed by field_key, used only in preview/print mode. */
  data?: Record<string, unknown>;
  /** Map of field_key -> human readable Persian label */
  fieldLabels?: Record<string, string>;
  /** Map of table field_key -> column definitions with labels */
  tableSchemas?: Record<string, TableColumnSchema[]>;
  selected?: boolean;
  selectedElementId?: string | null;
  onSelect?: (id: string) => void;
  onResize?: (id: string, w: number, h: number) => void;
  renderChild?: (child: CanvasElement) => React.ReactNode;
  /** If false, fills parent container (used inside DraggableResizableElement). Defaults to true. */
  standalone?: boolean;
  /** Optional pagination start row offset for preview mode */
  rowOffset?: number;
  /** Optional pagination row limit per page for preview mode */
  rowLimit?: number;
}

const FALLBACK_COLUMN_LABELS: Record<string, string> = {
  sequence_no: "ردیف",
  part_group: "گروه قطعه",
  item_name: "نام کامل قطعه",
  material_name: "نام قطعه",
  supplier: "تولیدکننده / تامین‌کننده",
  manufacturer_supplier: "تولیدکننده / تامین‌کننده",
  brand: "نام تجاری",
  raw_commercial_name: "نام تجاری",
  part_code: "کد فنی",
  material_sku: "کد فنی",
  standard_status: "وضعیت استاندارد",
  remarks: "توضیحات",
  notes: "توضیحات",
  quantity: "تعداد",
  production_uom_display: "واحد سنجش",
  scrap_percent: "درصد ضایعات",
};

const PERSIAN_TO_FIELD_KEY: Record<string, string> = {
  "ردیف": "sequence_no",
  "گروه قطعه": "group_name",
  "نام کامل قطعه": "item_name",
  "نام قطعه": "material_name",
  "تولیدکننده / تامین‌کننده": "supplier",
  "تولید کننده / تأمین کننده": "supplier",
  "تولیدکننده / تأمین کننده": "supplier",
  "نام تجاری": "commercial_name",
  "نوع": "product_type",
  "گونه": "product_variant",
  "مدل": "product_model",
  "کد فنی": "part_code",
  "وضعیت استاندارد": "standard_status",
  "توضیحات": "remarks",
  "تعداد": "quantity",
  "واحد سنجش": "production_uom_display",
  "درصد ضایعات": "scrap_percent",
};

const FIELD_KEY_ALIASES: Record<string, string[]> = {
  part_group: ["group_name", "part_group"],
  group_name: ["part_group", "group_name"],
  manufacturer_supplier: ["supplier", "manufacturer_supplier"],
  supplier: ["manufacturer_supplier", "supplier"],
  raw_commercial_name: ["brand", "raw_commercial_name"],
  brand: ["raw_commercial_name", "brand"],
  item_name: ["material_name", "item_name"],
  material_name: ["item_name", "material_name"],
  part_code: ["material_sku", "part_code"],
  material_sku: ["part_code", "material_sku"],
  product_type: ["vehicle_category", "type", "product_type"],
  vehicle_category: ["product_type", "type", "vehicle_category"],
  type: ["product_type", "vehicle_category", "type"],
  product_variant: ["kind", "variant", "product_variant"],
  kind: ["product_variant", "variant", "kind"],
  variant: ["product_variant", "kind", "variant"],
  product_model: ["model", "product_model"],
  model: ["product_model", "model"],
};

export function resolveColumnDataValue(row: Record<string, unknown>, colKey: string): unknown {
  if (colKey in row && row[colKey] !== undefined && row[colKey] !== null && row[colKey] !== "") {
    return row[colKey];
  }
  const canonicalKey = PERSIAN_TO_FIELD_KEY[colKey] || colKey;
  if (canonicalKey in row && row[canonicalKey] !== undefined && row[canonicalKey] !== null && row[canonicalKey] !== "") {
    return row[canonicalKey];
  }
  const aliases = FIELD_KEY_ALIASES[canonicalKey] || FIELD_KEY_ALIASES[colKey];
  if (aliases) {
    for (const alias of aliases) {
      if (alias in row && row[alias] !== undefined && row[alias] !== null && row[alias] !== "") {
        return row[alias];
      }
    }
  }
  return undefined;
}

function resolveTableRows(
  data: Record<string, unknown> | undefined,
  fieldKey: string,
  elementColumns: string[],
  columnSchemas?: TableColumnSchema[]
): Array<Record<string, unknown>> {
  if (data && fieldKey in data) {
    const value = data[fieldKey];
    return Array.isArray(value) ? (value as Array<Record<string, unknown>>) : [];
  }
  if (!data && elementColumns.length > 0) {
    const schemaMap = new Map(columnSchemas?.map((c) => [c.key, c.label]) ?? []);
    const cols = elementColumns.map((k) => ({
      key: k,
      label: schemaMap.get(k) || FALLBACK_COLUMN_LABELS[k] || k,
    }));
    return buildSampleTableRows(cols, 3);
  }
  return [];
}

export function evaluateElementDisplay(
  rawVal: unknown,
  rowData: Record<string, unknown> | undefined,
  element: { depends_on_field_key?: string; evaluator_type?: string; target_value?: string }
): string {
  // Check cascading dependency first
  if (element.depends_on_field_key && rowData) {
    const parentVal = rowData[element.depends_on_field_key];
    const isParentTruthy =
      parentVal !== false &&
      parentVal !== 0 &&
      parentVal !== "0" &&
      parentVal !== "false" &&
      parentVal != null &&
      parentVal !== "";
    if (!isParentTruthy) {
      return "-";
    }
  }

  const evaluatorType = element.evaluator_type || "raw";
  const isTruthy =
    rawVal !== false &&
    rawVal !== 0 &&
    rawVal !== "0" &&
    rawVal !== "false" &&
    rawVal != null &&
    rawVal !== "";

  if (evaluatorType === "boolean_check") {
    return isTruthy ? "✓" : "-";
  }

  if (evaluatorType === "equals_check") {
    const target = (element.target_value ?? "").trim();
    if (target === "1" || target.toLowerCase() === "true") {
      return isTruthy ? "✓" : "-";
    }
    if (target === "0" || target.toLowerCase() === "false") {
      return !isTruthy ? "✓" : "-";
    }
    const current = (rawVal != null ? String(rawVal) : "").trim();
    return current === target ? "✓" : "-";
  }

  if (evaluatorType === "not_equals_check") {
    const target = (element.target_value ?? "").trim();
    if (target === "1" || target.toLowerCase() === "true") {
      return !isTruthy ? "✓" : "-";
    }
    if (target === "0" || target.toLowerCase() === "false") {
      return isTruthy ? "✓" : "-";
    }
    const current = (rawVal != null ? String(rawVal) : "").trim();
    return current !== target ? "✓" : "-";
  }

  return rawVal != null ? String(rawVal) : "";
}

function QrCodeRenderer({ text, size }: { text: string; size: number }) {
  const [svgHtml, setSvgHtml] = useState<string>("");

  useEffect(() => {
    let active = true;
    if (!text || text.trim() === "") {
      setSvgHtml("");
      return;
    }
    QRCode.toString(text, { type: "svg", margin: 1, width: size })
      .then((svg) => {
        if (active) setSvgHtml(svg);
      })
      .catch(() => {
        if (active) setSvgHtml("");
      });
    return () => {
      active = false;
    };
  }, [text, size]);

  if (!svgHtml) {
    return (
      <div style={{ width: size, height: size, background: "#f9fafb", border: "1px dashed #e5e7eb", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "#9ca3af" }}>
        [QR خالی]
      </div>
    );
  }

  return (
    <div
      style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}
      dangerouslySetInnerHTML={{ __html: svgHtml }}
    />
  );
}

export function CanvasElementView({
  element,
  data,
  fieldLabels,
  tableSchemas,
  selected,
  selectedElementId,
  onSelect,
  onResize,
  renderChild,
  standalone = true,
  rowOffset = 0,
  rowLimit,
}: Props) {
  const borderStyle = element.border_style || "none";
  const borderWidth = element.border_width ?? 1;
  const borderColor = element.border_color || "#000000";

  const fontSize = element.font_size ?? 12;
  const fontWeight = element.font_weight || "normal";
  const fontFamily = element.font_family || "inherit";
  const textAlign = element.text_align || "right";

  const isPreviewOrPrint = Boolean(data);
  const showGuideOutline = !isPreviewOrPrint && borderStyle === "none";

  const style: CSSProperties = {
    position: standalone ? "absolute" : "relative",
    left: standalone ? element.x * GRID_UNIT_PX : undefined,
    top: standalone ? element.y * GRID_UNIT_PX : undefined,
    width: standalone ? element.w * GRID_UNIT_PX : "100%",
    height: standalone ? element.h * GRID_UNIT_PX : "100%",
    border: borderStyle !== "none" ? `${borderWidth}px ${borderStyle} ${borderColor}` : undefined,
    outline: selected
      ? "2px solid var(--designer-accent, #2563eb)"
      : showGuideOutline
      ? "1px dashed #d1d5db"
      : "none",
    borderRadius: 2,
    backgroundColor: element.background_color || "#ffffff",
    boxSizing: "border-box",
    padding: "2px 4px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    fontSize,
    fontWeight,
    fontFamily,
    textAlign,
    direction: "rtl",
    overflow: "hidden",
  };

  const handleClick = (e: React.MouseEvent) => {
    if ((e.nativeEvent as any)._clickHandled) return;
    (e.nativeEvent as any)._clickHandled = true;
    e.stopPropagation();
    if (onSelect) {
      onSelect(element.id);
    }
  };

  switch (element.type) {
    case "text": {
      let content = element.content || "";
      if (data && content.includes("{{")) {
        content = content.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, key) => {
          const val = resolveColumnDataValue(data, key);
          return val != null && val !== "" ? String(val) : "-";
        });
      }
      return (
        <div data-element-id={element.id} data-element-type="text" style={style} onClick={handleClick}>
          {content}
        </div>
      );
    }

    case "data_field": {
      let val: string;
      if (data) {
        val = evaluateElementDisplay(resolveColumnDataValue(data, element.field_key), data, element);
      } else {
        const label = fieldLabels?.[element.field_key] || FALLBACK_COLUMN_LABELS[element.field_key];
        val = label ? buildSampleValue(label) : `{{${element.field_key}}}`;
      }
      return (
        <div data-element-id={element.id} data-element-type="data_field" style={style} onClick={handleClick}>
          {val}
        </div>
      );
    }

    case "table": {
      const elementColumns = element.columns || [];
      const columnSchemas = tableSchemas?.[element.field_key];
      const allRows = resolveTableRows(data, element.field_key, elementColumns, columnSchemas);
      const rows = rowLimit != null ? allRows.slice(rowOffset, rowOffset + rowLimit) : (rowOffset > 0 ? allRows.slice(rowOffset) : allRows);

      const displayCols = elementColumns.map((colKey) => {
        const schema = columnSchemas?.find((c) => c.key === colKey);
        return { key: colKey, label: schema?.label || FALLBACK_COLUMN_LABELS[colKey] || colKey };
      });

      const tableContainerStyle: CSSProperties = data
        ? { ...style, height: undefined, maxHeight: element.h * GRID_UNIT_PX, overflow: "hidden", padding: 0 }
        : { ...style, overflow: "hidden", padding: 0 };

      return (
        <div
          data-element-id={element.id}
          data-element-type="table"
          style={tableContainerStyle}
          onClick={handleClick}
        >
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: Math.max(9, fontSize - 1) }}>
            <thead>
              <tr style={{ background: "#f3f4f6" }}>
                {displayCols.map((c) => (
                  <th key={c.key} style={{ border: "1px solid #d1d5db", padding: 2, textAlign: "center" }}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={displayCols.length || 1} style={{ color: "#9ca3af", padding: 2, textAlign: "center" }}>
                    (بدون داده — پیش‌نمایش خالی)
                  </td>
                </tr>
              ) : (
                rows.map((row, idx) => (
                  <tr key={idx}>
                    {displayCols.map((c) => (
                      <td key={c.key} style={{ border: "1px solid #e5e7eb", padding: 2, textAlign: "center" }}>
                        {evaluateElementDisplay(resolveColumnDataValue(row, c.key), row, element)}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      );
    }

    case "image":
      return (
        <div data-element-id={element.id} data-element-type="image" style={{ ...style, padding: 0 }} onClick={handleClick}>
          {element.src ? (
            <img src={element.src} alt="قالب" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
          ) : (
            <div style={{ width: "100%", height: "100%", background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "#9ca3af" }}>
              [تصویر بدون آدرس]
            </div>
          )}
        </div>
      );

    case "shape": {
      const strokeWidth = element.stroke_width ?? 1;
      const strokeColor = element.stroke_color || "#000000";
      const strokeStyle = element.stroke_style || "solid";
      const fillColor = element.fill_color || "transparent";

      const shapeStyle: CSSProperties = {
        position: standalone ? "absolute" : "relative",
        left: standalone ? element.x * GRID_UNIT_PX : undefined,
        top: standalone ? element.y * GRID_UNIT_PX : undefined,
        width: standalone ? element.w * GRID_UNIT_PX : "100%",
        height: standalone ? element.h * GRID_UNIT_PX : "100%",
        boxSizing: "border-box",
      };

      if (element.shape_kind === "line_h") {
        return (
          <div data-element-id={element.id} data-element-type="shape" style={{ ...shapeStyle, display: "flex", alignItems: "center" }} onClick={handleClick}>
            <div style={{ width: "100%", borderTop: `${strokeWidth}px ${strokeStyle} ${strokeColor}` }} />
          </div>
        );
      }

      if (element.shape_kind === "line_v") {
        return (
          <div data-element-id={element.id} data-element-type="shape" style={{ ...shapeStyle, display: "flex", justifyContent: "center" }} onClick={handleClick}>
            <div style={{ height: "100%", borderLeft: `${strokeWidth}px ${strokeStyle} ${strokeColor}` }} />
          </div>
        );
      }

      return (
        <div
          data-element-id={element.id}
          data-element-type="shape"
          style={{
            ...shapeStyle,
            border: `${strokeWidth}px ${strokeStyle} ${strokeColor}`,
            backgroundColor: fillColor,
          }}
          onClick={handleClick}
        />
      );
    }

    case "barcode": {
      let val = element.content || "";
      if (element.field_key) {
        if (data) {
          const raw = resolveColumnDataValue(data, element.field_key);
          val = raw != null && raw !== "" ? String(raw) : "";
        } else {
          val = element.content || (element.field_key ? "SAMPLE-" + element.field_key.toUpperCase() : "SAMPLE-LOT-01");
        }
      }
      const svg = generateBarcodeSvg(val, {
        width: element.w * GRID_UNIT_PX,
        height: element.h * GRID_UNIT_PX,
        showText: element.show_text ?? true,
      });

      const isDensityWarning = svg.includes('data-density-warning="true"');

      return (
        <div
          data-element-id={element.id}
          data-element-type="barcode"
          style={{ ...style, padding: 0, display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}
          onClick={handleClick}
        >
          <div dangerouslySetInnerHTML={{ __html: svg }} style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }} />
          {isDensityWarning && (
            <span
              data-testid="barcode-density-warning"
              title="هشدار تراکم بارکد: عرض ماژول کمتر از 0.75px است و ممکن است توسط اسکنر خوانده نشود."
              style={{
                position: "absolute",
                top: 2,
                right: 2,
                backgroundColor: "#f59e0b",
                color: "#ffffff",
                fontSize: "10px",
                borderRadius: "3px",
                padding: "1px 4px",
                lineHeight: 1.2,
                pointerEvents: "none",
                fontWeight: "bold",
                boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
              }}
            >
              ⚠️ تراکم بالا
            </span>
          )}
        </div>
      );
    }

    case "qr_code": {
      let val = element.content || "https://mimzad.ir";
      if (element.field_key) {
        if (data) {
          const raw = resolveColumnDataValue(data, element.field_key);
          val = raw != null && raw !== "" ? String(raw) : "";
        } else {
          const label = fieldLabels?.[element.field_key] || FALLBACK_COLUMN_LABELS[element.field_key];
          val = label ? label : element.field_key;
        }
      }
      const size = Math.min(element.w * GRID_UNIT_PX, element.h * GRID_UNIT_PX);

      return (
        <div
          data-element-id={element.id}
          data-element-type="qr_code"
          style={{ ...style, padding: 2, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={handleClick}
        >
          <QrCodeRenderer text={val} size={size} />
        </div>
      );
    }

    case "row_number": {
      const startFrom = element.start_from ?? 1;
      const val = data ? (data.__row_index != null ? Number(data.__row_index) + startFrom : startFrom) : `[#${startFrom}]`;
      return (
        <div data-element-id={element.id} data-element-type="row_number" style={style} onClick={handleClick}>
          {val}
        </div>
      );
    }

    case "group": {
      const repeatSource = element.repeat_source || "items";
      const groupName = element.name || "";
      const rowHeight = element.row_height || 1;
      const children = element.children || [];

      // Canvas mode (no data provided): render container + single template row with children
      if (!data) {
        return (
          <div
            data-element-id={element.id}
            data-element-type="group"
            style={{
              ...style,
              border: selected ? "2px solid #2563eb" : "1px dashed #2563eb",
              backgroundColor: "rgba(37, 99, 235, 0.03)",
              overflow: "visible",
            }}
            onClick={handleClick}
          >
            <div style={{ fontSize: 10, color: "#2563eb", fontWeight: "bold", padding: 4, background: "rgba(37, 99, 235, 0.08)", borderRadius: 3, marginBottom: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>گروه تکرار شونده: {groupName ? `${groupName} (${repeatSource})` : repeatSource}</span>
              <span style={{ fontSize: 9, color: "#6b7280" }}>شناسه: {element.id}</span>
            </div>
            <div style={{ position: "relative", width: "100%", height: Math.max(rowHeight * GRID_UNIT_PX, 40) }}>
              {children.length === 0 ? (
                <div style={{ color: "#9ca3af", fontSize: 11, padding: 8, textAlign: "center", border: "1px dashed #d1d5db", borderRadius: 4, margin: 4 }}>
                  گروه خالی است — فیلدها را از پنل سمت چپ به این گروه متصل کنید.
                </div>
              ) : (
                children.map((child) =>
                  renderChild ? (
                    renderChild(child)
                  ) : (
                    <CanvasElementView
                      key={child.id}
                      element={child}
                      selected={child.id === selectedElementId}
                      selectedElementId={selectedElementId}
                      onSelect={onSelect}
                      onResize={onResize}
                      fieldLabels={fieldLabels}
                      tableSchemas={tableSchemas}
                      standalone={true}
                    />
                  )
                )
              )}
            </div>
          </div>
        );
      }

      // Preview / Print mode (data provided): repeat children for each item in data[repeatSource]
      const itemsRaw = data[repeatSource];
      const allItems = Array.isArray(itemsRaw) ? (itemsRaw as Array<Record<string, unknown>>) : [];
      const items = rowLimit != null ? allItems.slice(rowOffset, rowOffset + rowLimit) : (rowOffset > 0 ? allItems.slice(rowOffset) : allItems);
      const sampleItems = items.length > 0 ? items : [{}, {}, {}];

      return (
        <div data-element-id={element.id} data-element-type="group" style={{ ...style, overflow: "visible" }} onClick={handleClick}>
          {sampleItems.map((item, idx) => {
            const itemData: Record<string, unknown> = { ...item, __row_index: idx };
            return (
              <div
                key={idx}
                style={{
                  position: "absolute",
                  top: idx * rowHeight * GRID_UNIT_PX,
                  left: 0,
                  width: "100%",
                  height: rowHeight * GRID_UNIT_PX,
                }}
              >
                {children.map((child) => (
                  <CanvasElementView
                    key={child.id}
                    element={child}
                    data={itemData}
                    fieldLabels={fieldLabels}
                    tableSchemas={tableSchemas}
                    standalone={true}
                  />
                ))}
              </div>
            );
          })}
        </div>
      );
    }

    default:
      return null;
  }
}
